import { useSupabase } from '@packages/supabase'
import {
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Code,
  Card,
  ActionIcon,
  Tooltip,
  Switch,
  Grid,
  Collapse
} from '@mantine/core'
import { FilterInput, H1, H3, PageContentPaper } from '@packages/ui'
import {
  IconEdit,
  IconCheck,
  IconCopy,
  IconRefresh,
  IconEye
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

interface Prompt {
  id: string
  slug: string
  name: string
  version: number
  is_active: boolean
  template: string
  content: string
  metadata: any
  created_at: string
  updated_at: string
}

export const Prompts = () => {
  const { supabase } = useSupabase()
  const [prompts, setPrompts] = useState<Prompt[]>([])
  const [loading, setLoading] = useState(true)
  const [filterBy, setFilterBy] = useState('')
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingAsNew, setSavingAsNew] = useState(false)

  // Editor state
  const [editedTemplate, setEditedTemplate] = useState('')
  const [editedName, setEditedName] = useState('')

  // Preview state
  const [showRendered, setShowRendered] = useState(false)
  const [previewParams, setPreviewParams] = useState<Record<string, any>>({
    user_language: 'de',
    tenant_language: 'en',
    user_name: 'Florian',
    expect_greeting: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    current_date_iso: new Date().toISOString(),
    current_local_date: new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }),
    current_local_time: new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }),
    current_unix_ms: Date.now(),
    current_day_of_week: new Date().toLocaleDateString('en-US', {
      weekday: 'long'
    })
  })

  const fetchPrompts = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('ai_prompts')
        .select('*')
        .order('slug', { ascending: true })
        .order('version', { ascending: false })

      if (error) throw error
      setPrompts(data || [])
    } catch (error) {
      console.error('Error fetching prompts:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPrompts()
  }, [])

  // Group prompts by slug
  const promptsBySlug = useMemo(() => {
    const grouped: Record<string, Prompt[]> = {}
    prompts.forEach((prompt) => {
      if (!grouped[prompt.slug]) {
        grouped[prompt.slug] = []
      }
      grouped[prompt.slug].push(prompt)
    })
    return grouped
  }, [prompts])

  // Filter slugs
  const filteredSlugs = useMemo(() => {
    const slugs = Object.keys(promptsBySlug)
    if (!filterBy) return slugs
    return slugs.filter((slug) =>
      slug.toLowerCase().includes(filterBy.toLowerCase())
    )
  }, [promptsBySlug, filterBy])

  const openEditor = (prompt: Prompt) => {
    setEditingPrompt(prompt)
    // Use template if available, otherwise fall back to content (legacy)
    setEditedTemplate(prompt.template || prompt.content || '')
    setEditedName(prompt.name || '')
    setShowRendered(false) // Reset preview toggle
    setEditorOpen(true)
  }

  // Render template with preview parameters (same logic as edge function)
  const renderPreview = (
    template: string,
    params: Record<string, any>
  ): string => {
    let rendered = template

    // Handle conditional blocks: {{#if condition}}...{{else}}...{{/if}}
    const conditionalPattern =
      /\{\{#if\s+(\w+)\}\}([\s\S]*?)(?:\{\{else\}\}([\s\S]*?))?\{\{\/if\}\}/g
    rendered = rendered.replace(
      conditionalPattern,
      (
        match: string,
        paramName: string,
        ifContent: string,
        elseContent?: string
      ) => {
        const paramValue = params[paramName]
        const isTruthy =
          paramValue !== null &&
          paramValue !== undefined &&
          paramValue !== false &&
          paramValue !== '' &&
          paramValue !== 0
        return isTruthy ? ifContent : elseContent || ''
      }
    )

    // Replace all placeholders
    for (const [key, value] of Object.entries(params)) {
      const placeholder = `{{${key}}}`
      const replacement =
        value !== null && value !== undefined ? String(value) : ''
      rendered = rendered.replace(new RegExp(placeholder, 'g'), replacement)
    }

    return rendered
  }

  const renderedPreview = useMemo(() => {
    if (!editedTemplate) return ''

    // Update date/time values when rendering preview (so they're current)
    const now = new Date()
    const paramsWithCurrentTime = {
      ...previewParams,
      current_date_iso: now.toISOString(),
      current_local_date: now.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      current_local_time: now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }),
      current_unix_ms: Date.now(),
      current_day_of_week: now.toLocaleDateString('en-US', { weekday: 'long' })
    }

    return renderPreview(editedTemplate, paramsWithCurrentTime)
  }, [editedTemplate, previewParams])

  const closeEditor = () => {
    setEditorOpen(false)
    setEditingPrompt(null)
    setEditedTemplate('')
    setEditedName('')
  }

  const handleSave = async () => {
    if (!editingPrompt) return

    setSaving(true)
    try {
      // Update existing prompt
      // Note: content field is required (legacy), so we copy template to content for backward compatibility
      const { error } = await supabase
        .from('ai_prompts')
        .update({
          template: editedTemplate,
          content: editedTemplate, // Copy template to content for backward compatibility
          name: editedName,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingPrompt.id)

      if (error) {
        console.error('Supabase error details:', error)
        throw error
      }

      // Refresh prompts list
      // await fetchPrompts()

      // Update the editingPrompt state with the saved data (keep editor open)
      const updatedPrompt: Prompt = {
        ...editingPrompt,
        template: editedTemplate,
        content: editedTemplate,
        name: editedName,
        updated_at: new Date().toISOString()
      }
      setEditingPrompt(updatedPrompt)
    } catch (error) {
      console.error('Error saving prompt:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'message' in error
            ? String(error.message)
            : 'Unknown error occurred'
      alert('Failed to save prompt: ' + errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAsNewVersion = async () => {
    if (!editingPrompt) return

    setSavingAsNew(true)
    try {
      // First, deactivate all versions of this slug
      await supabase
        .from('ai_prompts')
        .update({ is_active: false })
        .eq('slug', editingPrompt.slug)

      // Get the highest version number
      const versions = promptsBySlug[editingPrompt.slug] || []
      const maxVersion = Math.max(...versions.map((p) => p.version || 1), 0)
      const newVersion = maxVersion + 1

      // Create new version
      // Note: content field is required (legacy), so we copy template to content for backward compatibility
      const { error } = await supabase.from('ai_prompts').insert({
        slug: editingPrompt.slug,
        name: editedName,
        version: newVersion,
        is_active: true,
        template: editedTemplate,
        content: editedTemplate, // Copy template to content for backward compatibility
        metadata: editingPrompt.metadata || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })

      if (error) throw error

      await fetchPrompts()
      closeEditor()
    } catch (error) {
      console.error('Error saving as new version:', error)
      alert('Failed to save as new version: ' + (error as Error).message)
    } finally {
      setSavingAsNew(false)
    }
  }

  const getActiveVersion = (slug: string): Prompt | undefined => {
    const versions = promptsBySlug[slug] || []
    return versions.find((p) => p.is_active) || versions[0]
  }

  return (
    <Stack>
      <Group justify="space-between">
        <H1 className={'text-white'}>AI Prompt Manager</H1>
        <Button
          variant="light"
          leftSection={<IconRefresh size={16} />}
          onClick={fetchPrompts}
          loading={loading}
        >
          Refresh
        </Button>
      </Group>

      <Group>
        <FilterInput onFilter={setFilterBy} filter={filterBy} />
      </Group>

      <PageContentPaper c={'white'}>
        {loading ? (
          <Text ta="center">Loading...</Text>
        ) : filteredSlugs.length === 0 ? (
          <Text ta="center">No prompts found</Text>
        ) : (
          <Table className={'overflow-hidden rounded-md bg-white/10'}>
            <Table.Thead className={'bg-white/20'}>
              <Table.Tr>
                <Table.Th>Slug</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Active Version</Table.Th>
                <Table.Th>Total Versions</Table.Th>
                <Table.Th>Last Updated</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredSlugs.map((slug) => {
                const activeVersion = getActiveVersion(slug)
                const versions = promptsBySlug[slug] || []
                const latestVersion = versions[0] // Already sorted by version DESC

                return (
                  <Table.Tr
                    key={slug}
                    className="cursor-pointer hover:bg-white/10"
                    onClick={() =>
                      setSelectedSlug(selectedSlug === slug ? null : slug)
                    }
                  >
                    <Table.Td>
                      <Text>'{slug}'</Text>
                    </Table.Td>
                    <Table.Td>{activeVersion?.name || '-'}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Badge
                          color={activeVersion?.is_active ? 'green' : 'gray'}
                          size="sm"
                        >
                          v{activeVersion?.version || 1}
                        </Badge>
                        {activeVersion?.is_active && (
                          <Badge color="green" variant="light" size="xs">
                            Active
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>{versions.length}</Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {latestVersion?.updated_at
                          ? dayjs(latestVersion.updated_at).format(
                              'MMM D, YYYY'
                            )
                          : '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Edit active version">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (activeVersion) openEditor(activeVersion)
                            }}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        )}

        {/* Version details when slug is expanded */}
        {selectedSlug && promptsBySlug[selectedSlug] && (
          <Stack c={'white'} className={'rounded-md bg-white/10 p-3'}>
            <H3>Versions for: {selectedSlug}</H3>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Version</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {promptsBySlug[selectedSlug].map((prompt) => (
                  <Table.Tr key={prompt.id}>
                    <Table.Td>
                      <Badge
                        color={prompt.is_active ? 'green' : 'gray'}
                        size="sm"
                      >
                        v{prompt.version}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={prompt.is_active ? 'green' : 'gray'}
                        variant="light"
                        size="sm"
                      >
                        {prompt.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {dayjs(prompt.created_at).format('MMM D, YYYY h:mm A')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="Edit">
                          <ActionIcon
                            variant="light"
                            color="blue"
                            size="sm"
                            onClick={() => openEditor(prompt)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="View template">
                          <ActionIcon
                            variant="light"
                            color="gray"
                            size="sm"
                            onClick={() => {
                              const preview =
                                prompt.template || prompt.content || ''
                              alert(
                                preview.substring(0, 500) +
                                  (preview.length > 500 ? '...' : '')
                              )
                            }}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        )}
      </PageContentPaper>

      {/* Editor Modal */}
      <Modal
        opened={editorOpen}
        onClose={closeEditor}
        title={
          <Group>
            <Text fw={700}>Edit Prompt</Text>
            {editingPrompt && (
              <Badge color="blue" variant="light">
                {editingPrompt.slug} v{editingPrompt.version}
              </Badge>
            )}
          </Group>
        }
        size="xl"
      >
        {editingPrompt && (
          <Stack gap="md">
            <TextInput
              label="Name"
              value={editedName}
              onChange={(e) => setEditedName(e.currentTarget.value)}
              placeholder="Prompt name"
            />

            {/* Preview Parameters */}
            <Card withBorder p="md">
              <Group justify="space-between" mb="sm">
                <Text fw={500}>Preview Parameters</Text>
                <Switch
                  label="Show Rendered Preview"
                  checked={showRendered}
                  onChange={(e) => setShowRendered(e.currentTarget.checked)}
                />
              </Group>

              <Collapse expanded>
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label="User Language"
                      value={previewParams.user_language || ''}
                      onChange={(e) =>
                        setPreviewParams({
                          ...previewParams,
                          user_language: e.currentTarget.value
                        })
                      }
                      placeholder="e.g., de, en, fr"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Tenant Language"
                      value={previewParams.tenant_language || ''}
                      onChange={(e) =>
                        setPreviewParams({
                          ...previewParams,
                          tenant_language: e.currentTarget.value
                        })
                      }
                      placeholder="e.g., en"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="User Name"
                      value={previewParams.user_name || ''}
                      onChange={(e) =>
                        setPreviewParams({
                          ...previewParams,
                          user_name: e.currentTarget.value
                        })
                      }
                      placeholder="e.g., Florian"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Timezone"
                      value={previewParams.timezone || ''}
                      onChange={(e) =>
                        setPreviewParams({
                          ...previewParams,
                          timezone: e.currentTarget.value
                        })
                      }
                      placeholder="e.g., Europe/Berlin"
                    />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Switch
                      label="Expect Greeting"
                      checked={previewParams.expect_greeting === true}
                      onChange={(e) =>
                        setPreviewParams({
                          ...previewParams,
                          expect_greeting: e.currentTarget.checked
                        })
                      }
                    />
                  </Grid.Col>
                </Grid>
              </Collapse>
            </Card>

            {/* Template/Rendered Preview */}
            {showRendered ? (
              <Textarea
                label="Rendered Preview (with parameters filled in)"
                value={renderedPreview}
                readOnly
                minRows={20}
                autosize
                styles={{
                  input: {
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    backgroundColor: '#f8f9fa'
                  }
                }}
              />
            ) : (
              <Textarea
                label="Template (with placeholders like {{user_language}}, {{user_name}}, etc.)"
                value={editedTemplate}
                onChange={(e) => setEditedTemplate(e.currentTarget.value)}
                placeholder="Enter prompt template with placeholders..."
                minRows={20}
                autosize
                styles={{
                  input: {
                    fontFamily: 'monospace',
                    fontSize: '13px'
                  }
                }}
              />
            )}

            <Card withBorder p="xs">
              <Text size="xs" c="dimmed">
                <strong>Template:</strong> Use placeholders like{' '}
                <Code>{'{{user_language}}'}</Code>,{' '}
                <Code>{'{{user_name}}'}</Code>,{' '}
                <Code>{'{{tenant_language}}'}</Code>, etc. These will be
                replaced with actual values when the prompt is rendered.
                <br />
                <strong>Save as New Version:</strong> Creates a new version and
                deactivates the current active version.
              </Text>
            </Card>

            <Group justify="flex-end">
              <Button
                variant="light"
                onClick={closeEditor}
                disabled={saving || savingAsNew}
              >
                Cancel
              </Button>
              <Button
                variant="light"
                color="blue"
                leftSection={<IconCopy size={16} />}
                onClick={handleSaveAsNewVersion}
                loading={savingAsNew}
                disabled={saving}
              >
                Save as New Version
              </Button>
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={handleSave}
                loading={saving}
                disabled={savingAsNew}
              >
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
